// components/EventDetailMobile.js
import React from 'react';
import styled from 'styled-components';
import Typography from '../../components/Typography';
import Tag from '../../components/Tag';
import InfoCircleIcon from '../../components/Icons/InfoCircleIcon';
import { theme } from '../../theme';
import { getVehicleTypeLabel, ALL_VEHICLE_TYPES } from '../../constants/vehicleTypes';
import i18n from '../../i18n';
import OrganizerBlock from '../../components/EventContent/OrganizerBlock';
import DateAndLocation from '../../components/EventSummary/DateAndLocation';
import Attendees from '../../components/EventSummary/Attendees';
import PastEventBanner from '../../components/EventSummary/PastEventBanner';
import { useTranslation } from 'react-i18next';

const EventDetailMobile = ({ event, organizer, isEnrolled, status, ticket, attendee, priorAttendee, handleCancelEnrollment, handleContactOrganizer, onShowAllAttendees, isLoggedIn, onViewDetails, hasPassed, hasStarted, allowedVehicleTypes = [], needsVehicle = true }) => {
  const { t: tEventDetail } = useTranslation('eventDetail');
  if (!event || !organizer) {
    return null; // O un loader
  }

  const {
    _id: eventId,
    image,
    description,
    location,
    shortLocation,
    mapCoordinates,
    attendees,
    attendeesCount,
    startDate,
    endDate,
  } = event;

  // Formatear el objeto date para pasarlo al componente DateAndLocation
  const formattedDate = {
    startDate,
    endDate,
  };


  return (
    <MobileContainer>
      {/* 1. Event Image */}
      <EventImage src={image} alt="Imagen del evento" />

      {/* Past Event Banner */}
      {hasPassed && (
        <PastEventBannerContainer>
          <PastEventBanner eventEndDate={event.endDate} hideIcon={true} borderRadius="8px" />
        </PastEventBannerContainer>
      )}

      {/* 2. Event Date and Location */}
      <DateAndLocation
        date={formattedDate} // Usar el objeto formattedDate con todas las propiedades necesarias
        location={location}
        shortLocation={shortLocation}
        mapCoordinates={mapCoordinates}
        containerBorderRadius="0px"
        containerBorderColor="transparent"
        mapMaxHeight="250px"
        mapMobileHeight="300px"
        mapBorderRadius="16px"
        mapPadding="16px"
        mapMargin="0px"
      />

      {/* 3. Details (description) */}
      <EventDescription>
        <Typography $variant="title-4-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold" as='p'>
          {tEventDetail('eventDescription.title')}
        </Typography>
        <Typography $variant="body-1-regular">{description}</Typography>
      </EventDescription>

      {/* 4. Allowed vehiciles if apply */}
      {event?.needsVehicle && (
        <EventAllowedVehiclesMobile>
          {(
            !allowedVehicleTypes || allowedVehicleTypes.length === 0 || allowedVehicleTypes.length === ALL_VEHICLE_TYPES.length
          ) ? (
            <>
              <AllowedVehiclesHeaderMobile>
                <AllowedVehiclesTitleMobile>
                  <InfoCircleIcon fill={theme.colors.defaultStrong} width={16} height={16} />
                  <Typography color={theme.colors.defaultStrong} $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" as='p'>{tEventDetail('allowedVehicles.title', 'Este evento requiere un vehiculo')}</Typography>
                </AllowedVehiclesTitleMobile>
                <Typography color={theme.colors.defaultWeak} $variant="body-2-medium">
                  {tEventDetail('allowedVehicles.baseDescription', 'Para inscribirte necesitas indicar el vehiculo con el que asistirás.')}
                </Typography>
              </AllowedVehiclesHeaderMobile>
            </>
          ) : (
            <>
              <AllowedVehiclesHeaderMobile>
                <AllowedVehiclesTitleMobile>
                  <InfoCircleIcon fill={theme.colors.defaultStrong} width={16} height={16} />
                  <Typography color={theme.colors.defaultStrong} $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" as='p'>{tEventDetail('allowedVehicles.title', 'Este evento requiere un vehiculo')}</Typography>
                </AllowedVehiclesTitleMobile>
                <Typography color={theme.colors.defaultWeak} $variant="body-2-medium">
                  {tEventDetail('allowedVehicles.restrictedDescription', 'Para inscribirte necesitas indicar el vehiculo con el que asistirás. Los tipos vehiculos permitidos son:')}
                </Typography>
              </AllowedVehiclesHeaderMobile>
              <TagsListMobile>
                {(allowedVehicleTypes || event?.allowedVehicleTypes || []).map((type, idx) => (
                  <Tag key={`allowed-${type}-${idx}`} $variant="subtle" $letterSpacing='0' $textTransform="none" $textStyle="body-3-semibold" $borderRadius="12px">
                    {getVehicleTypeLabel(type)}
                  </Tag>
                ))}
              </TagsListMobile>
            </>
          )}
        </EventAllowedVehiclesMobile>
      )}

      {/* 5. Attendees */}
      <Attendees
        attendees={attendees}
        attendeesCount={attendeesCount}
        organizer={organizer}
        organizerVehicle={event.organizerVehicle}
        containerBorderRadius="0px"
        containerBorderColor="transparent"
        onShowAllAttendees={onShowAllAttendees}
        isLoggedIn={isLoggedIn}
      />


      {/* 5. Organizer bio and info */}
      <OrganizerSection>
        <OrganizerBlock organizer={organizer} eventId={eventId} />
      </OrganizerSection>

    </MobileContainer>
  );
};

export default EventDetailMobile;

const EventImage = styled.img`
  aspect-ratio: 4/3;
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  height: auto;
  object-fit: cover;
  width: 100%;
`;

const EventDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.md} ${({ theme }) => theme.sizing.sm}  ${({ theme }) => theme.sizing.lg}  ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.sizing.xs};
    padding: ${({ theme }) => theme.sizing.sm};
  }
  `;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 80px; /* Espacio para el MobileBottomNav */
  width: 100%;
`;

const OrganizerSection = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
`;

const PastEventBannerContainer = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
  padding-bottom: 0px;
`;

const EventAllowedVehiclesMobile = styled.div`
  margin: ${({ theme }) => theme.sizing.sm};
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.sizing.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  // padding: ${({ theme }) => theme.sizing.sm};
  padding: 12px;
`;

const VehicleTag = styled(Tag)`
  padding: 4px;
`;

const AllowedVehiclesHeaderMobile = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const AllowedVehiclesTitleMobile = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const TagsListMobile = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.sizing.xxs};
`;
